import { flexRender, type RowData } from '@tanstack/react-table';
import * as React from 'react';

import { DimeTableCell, DimeTableRow } from '../DimeTable/index.js';
import { cn } from '../lib/utils.js';
import type { TreeRow } from './DimeTreeTable.types.js';
import { rowSiblingPosition } from './row-sibling-position.js';
import { TreeExpandToggle } from './TreeExpandToggle.js';

interface TreeTableRowProps<TData extends RowData> {
  row: TreeRow<TData>;
  /** Every visible row grouped by parentId (row-sibling-position.ts) — the sibling set for this
   *  row is whichever group its own parentId maps to. */
  siblingGroups: ReadonlyMap<string, readonly TreeRow<TData>[]>;
  indentColumnId: string;
  indentSize: number;
  isSelected: boolean;
  isActiveRow: boolean;
  rowClassName?: string;
  registerRowElement: (rowId: string, element: HTMLTableRowElement | null) => void;
  onRowFocus: (rowId: string) => void;
  onRowBlur: (event: React.FocusEvent) => void;
  onRowKeyDown: (event: React.KeyboardEvent, row: TreeRow<TData>) => void;
  onToggleExpand: (row: TreeRow<TData>) => void;
  onSelectRow?: (row: TreeRow<TData>) => void;
  onRowClick?: (row: TreeRow<TData>) => void;
}

/** One node in the tree — a single real `role="row"` at its own depth. There is no separate
 *  presentational panel row the way DimeNestedTable has for its child block: every descendant is
 *  its own row in the same grid, indented in the `indentColumnId` cell. */
export function TreeTableRow<TData extends RowData>({
  row,
  siblingGroups,
  indentColumnId,
  indentSize,
  isSelected,
  isActiveRow,
  rowClassName,
  registerRowElement,
  onRowFocus,
  onRowBlur,
  onRowKeyDown,
  onToggleExpand,
  onSelectRow,
  onRowClick,
}: Readonly<TreeTableRowProps<TData>>) {
  const canExpand = row.getCanExpand();
  const isExpanded = row.getIsExpanded();
  const { posinset, setsize } = rowSiblingPosition(row, siblingGroups);

  // onSelectRow (picker mode) always wins when both are given -- see DimeTreeTable.types.ts's
  // doc comment. Otherwise a click on an expandable row toggles it, matching the chevron, and
  // only a leaf click reaches the consumer's onRowClick.
  const handleRowClick = onSelectRow
    ? () => onSelectRow(row)
    : onRowClick
      ? () => {
          if (canExpand) {
            onToggleExpand(row);
          } else {
            onRowClick(row);
          }
        }
      : undefined;

  return (
    <DimeTableRow
      ref={(element: HTMLTableRowElement | null) => registerRowElement(row.id, element)}
      aria-expanded={canExpand ? isExpanded : undefined}
      // TanStack depth is 0-based; ARIA level is 1-based.
      aria-level={row.depth + 1}
      // Computed within the row's OWN sibling set (row-sibling-position.ts), not the flattened
      // visible list — a treegrid row states its place among its siblings, not among every
      // currently-visible row at every depth.
      aria-posinset={posinset}
      aria-setsize={setsize}
      aria-selected={onSelectRow ? isSelected : undefined}
      tabIndex={isActiveRow ? 0 : -1}
      onFocus={() => onRowFocus(row.id)}
      onBlur={onRowBlur}
      // React bubbles events out of portals, so a dialog opened from a cell would otherwise lose Space and Enter to the row.
      onKeyDown={(event) => {
        if (event.currentTarget.contains(event.target as Node)) onRowKeyDown(event, row);
      }}
      onClick={
        handleRowClick &&
        ((event) => {
          if (event.currentTarget.contains(event.target as Node)) handleRowClick();
        })
      }
      className={cn(
        handleRowClick && 'cursor-pointer',
        rowClassName,
        isSelected && 'bg-accent aria-expanded:bg-accent',
      )}
    >
      {row.getVisibleCells().map((cell) => (
        <DimeTableCell
          key={cell.id}
          className={cell.column.id === indentColumnId ? 'relative' : undefined}
        >
          {cell.column.id === indentColumnId ? (
            <>
              {/* Just the immediate parent's line, not the whole ancestor chain -- one per ancestor stacked up at every depth read as a grid, not a tree. */}
              {row.depth > 0 && (
                <span
                  data-slot="tree-guide-line"
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-y-0 w-px bg-border/60"
                  style={{ insetInlineStart: (row.depth - 1) * indentSize + indentSize / 2 }}
                />
              )}
              {/* Inline style, not a Tailwind class: the indent is a computed multiple of depth
               *  that no fixed utility class set can express, and the chevron renders inside this
               *  same cell before the value so there is no separate, un-indentable expand column
               *  like DimeNestedTable's expand-column.tsx -- the largest structural divergence
               *  this component avoids. */}
              <div
                className="flex items-center gap-1"
                style={{ paddingInlineStart: row.depth * indentSize }}
              >
                <TreeExpandToggle
                  canExpand={canExpand}
                  isExpanded={isExpanded}
                  onClick={() => onToggleExpand(row)}
                />
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </div>
            </>
          ) : (
            flexRender(cell.column.columnDef.cell, cell.getContext())
          )}
        </DimeTableCell>
      ))}
    </DimeTableRow>
  );
}
